import { useEffect, useState } from 'react';
import {
  Button,
  FileInput,
  Group,
  Input,
  Modal,
  Radio,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { DateTimePicker, Icon } from '@/components';
import {
  type DisplayStyleConfig,
  type SuggestedModule,
  type SuggestedModuleCreateRequest,
  SuggestedModuleDisplayStyle,
  type SuggestedModuleFormState,
  SuggestedModuleType,
  VideoQuantity,
} from '@/types/suggestedModules';
import { suggestedModulesService } from '@/services/suggested-modules.service';
import { showError } from '@/shared/utils/errors';
import { DisplayStyleOption } from './DisplayStyleOption';
import styles from './SuggestedModuleModal.module.sass';
import { DISPLAY_STYLES, INITIAL_FORM_STATE, MODULE_TYPES } from '@/constants';
import browserWindow from '@/assets/browser-window.png';
import type { DateStringValue } from '@mantine/dates';

type SuggestedModuleModalProps = {
  opened: boolean;
  onClose: () => void;
  onSave: (data: SuggestedModuleCreateRequest) => void;
  initialData?: SuggestedModule | null;
  isLoading?: boolean;
};

export const SuggestedModuleModal = ({
  opened,
  onClose,
  onSave,
  initialData,
  isLoading = false,
}: SuggestedModuleModalProps) => {
  const [formData, setFormData] = useState<SuggestedModuleFormState>(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        description: initialData.description,
        moduleType: initialData.moduleType,
        url: initialData.url,
        displayStyle: initialData.displayStyle,
        isVideoMultiple: initialData.isVideoMultiple,
        thumbnailPreview: initialData.thumbnailUrl,
        thumbnailPath: initialData.thumbnailPath,
        startDate: initialData.startDate,
        endDate: initialData.endDate,
      });
      setThumbnailFile(null);
    } else {
      setFormData(INITIAL_FORM_STATE);
      setThumbnailFile(null);
    }
    setErrors({});
  }, [initialData, opened]);

  const handleChange = (field: keyof SuggestedModuleFormState, value: unknown) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      if (field === 'moduleType') {
        if (value === SuggestedModuleType.VIDEO || value === SuggestedModuleType.WEB_LINK) {
          newData.displayStyle = SuggestedModuleDisplayStyle.BANNER;
        }
      }

      if (field === 'isVideoMultiple' && value === false) {
        newData.displayStyle = SuggestedModuleDisplayStyle.BANNER;
      }

      return newData;
    });

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Module Name is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (formData.description.length > 200) newErrors.description = 'Description cannot exceed 200 characters';
    if (!formData.url.trim()) newErrors.url = 'URL Link is required';
    try {
      new URL(formData.url);
    } catch {
      newErrors.url = 'Please enter a valid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleThumbnailUpload = async () => {
    if (!thumbnailFile) return null;
    setIsUploading(true);
    try {
      const response = await suggestedModulesService.uploadThumbnail(thumbnailFile);
      return response.data.path;
    } catch (error) {
      showError(error);
      return undefined;
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    let finalThumbnailPath = formData.thumbnailPath;
    if (!formData.thumbnailPreview) {
      finalThumbnailPath = null;
    }

    if (thumbnailFile) {
      const uploadedPath = await handleThumbnailUpload();
      if (uploadedPath === undefined) return; // Error occurred
      if (uploadedPath) finalThumbnailPath = uploadedPath;
    }

    const payload: SuggestedModuleCreateRequest = {
      ...formData,
      thumbnail: finalThumbnailPath ? { path: finalThumbnailPath } : null,
    };

    if (payload.thumbnail && initialData && thumbnailFile) {
      payload.thumbnail.removeCurrentThumbnail = true;
    }

    onSave(payload);
  };

  const getDisplayStyleProps = (option: DisplayStyleConfig) => {
    let isDisabled = false;
    let badge = option.badge;

    const isWebLink = formData.moduleType === SuggestedModuleType.WEB_LINK;
    const isSingleVideo = formData.moduleType === SuggestedModuleType.VIDEO && !formData.isVideoMultiple;

    if (isWebLink || isSingleVideo) {
      if (option.value === SuggestedModuleDisplayStyle.CARD || option.value === SuggestedModuleDisplayStyle.LIST) {
        isDisabled = true;
        badge = undefined;
      }
      if (option.value === SuggestedModuleDisplayStyle.BANNER) {
        badge = 'REQUIRED';
      }
    }

    if (formData.moduleType === SuggestedModuleType.VIDEO && formData.isVideoMultiple) {
      if (option.value === SuggestedModuleDisplayStyle.LIST) {
        badge = undefined;
      }
    }

    return { isDisabled, badge };
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={initialData ? 'Edit Suggested Module' : 'New Suggested Module'}
      size="lg"
      classNames={{
        header: styles.modalHeader,
        title: styles.modalTitle,
        close: styles.closeButton,
      }}
    >
      <Stack gap="md" pt="md">
        <TextInput
          label="Module Name"
          placeholder="Module Name"
          required
          value={formData.name}
          onChange={(e) => handleChange('name', e.currentTarget.value)}
          error={errors.name}
        />

        <Stack gap={0}>
          <Textarea
            label="Description"
            placeholder="Lorem ipsum dolor sit amet..."
            required
            value={formData.description}
            onChange={(e) => handleChange('description', e.currentTarget.value)}
            maxLength={200}
            error={errors.description}
          />
          <Text className={styles.charCount}>{formData.description.length}/200</Text>
        </Stack>

        <Group grow>
          <DateTimePicker
            label="Start Date"
            value={formData.startDate ? new Date(formData.startDate) : null}
            onChange={(date: DateStringValue | null) =>
              handleChange('startDate', date ? new Date(date).toISOString() : '')
            }
          />
          <DateTimePicker
            label="End Date"
            value={formData.endDate ? new Date(formData.endDate) : null}
            onChange={(date: DateStringValue | null) =>
              handleChange('endDate', date ? new Date(date).toISOString() : '')
            }
          />
        </Group>

        <Input.Wrapper
          label="Module Type"
          required
          description="Deliver real-time updates to your members"
          inputWrapperOrder={['label', 'input', 'description', 'error']}
        >
          <SegmentedControl
            data={MODULE_TYPES}
            value={formData.moduleType as string}
            onChange={(value) => handleChange('moduleType', value as SuggestedModuleType)}
            fullWidth
            classNames={{
              root: styles.moduleTypeSegmentedControl,
              label: styles.moduleTypeLabel,
            }}
          />
        </Input.Wrapper>

        <TextInput
          label={
            <Group gap={4}>
              <Text span size="sm" fw={500}>
                URL Link <span style={{ color: 'var(--mantine-color-error)' }}>*</span>
              </Text>
              <Tooltip
                label={
                  <Stack gap="xs" p={4}>
                    <Text size="xs">
                      URL is the “web address”. You can find it in the browser’s top bar, copy and paste is to here
                    </Text>
                    <img src={browserWindow} alt="Browser URL example" style={{ width: '100%', borderRadius: 4 }} />
                  </Stack>
                }
                multiline
                w={300}
                withArrow
                position="bottom-start"
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Icon name="IconInfoCircle" size={16} style={{ cursor: 'help', opacity: 0.5 }} />
                </div>
              </Tooltip>
            </Group>
          }
          placeholder="URL Link"
          withAsterisk={false}
          leftSection={<Icon name="IconLink" size={16} />}
          value={formData.url}
          onChange={(e) => handleChange('url', e.currentTarget.value)}
          error={errors.url}
        />

        <Input.Wrapper label="Display Style" required>
          <div className={styles.displayStyleContainer}>
            {DISPLAY_STYLES.map((option) => {
              const { isDisabled, badge } = getDisplayStyleProps(option);
              return (
                <DisplayStyleOption
                  key={option.value}
                  option={option}
                  isSelected={formData.displayStyle === option.value}
                  isDisabled={isDisabled}
                  badge={badge}
                  onClick={(value) => handleChange('displayStyle', value)}
                />
              );
            })}
          </div>
        </Input.Wrapper>

        {formData.moduleType === SuggestedModuleType.VIDEO && (
          <Radio.Group
            label="Video Quantity"
            required
            value={formData.isVideoMultiple ? VideoQuantity.MULTIPLE : VideoQuantity.SINGLE}
            onChange={(val) => handleChange('isVideoMultiple', val === VideoQuantity.MULTIPLE)}
          >
            <Group grow align="flex-start" mt="xs">
              <Radio value={VideoQuantity.SINGLE} label="Single" description="There is only one video from this link" />
              <Radio
                value={VideoQuantity.MULTIPLE}
                label="Multiple"
                description="There are recurring video from this link"
              />
            </Group>
          </Radio.Group>
        )}

        {formData.moduleType === SuggestedModuleType.WEB_LINK && (
          <Group align="stretch" wrap="nowrap">
            <FileInput
              style={{ flex: 1 }}
              label="Thumbnail Image"
              placeholder="Thumbnail Image"
              leftSection={<Icon name="IconUpload" size={16} />}
              description="Pro tip: This image will appear on your homepage for the module, add an image to make this module shine and engage members!"
              inputWrapperOrder={['label', 'input', 'description', 'error']}
              value={thumbnailFile}
              onChange={(file) => {
                setThumbnailFile(file);
                if (file) {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    handleChange('thumbnailPreview', reader.result as string);
                  };
                  reader.readAsDataURL(file);
                } else {
                  handleChange('thumbnailPreview', null);
                }
              }}
              accept="image/png,image/jpeg"
              clearable
            />
            {formData.thumbnailPreview && (
              <div className={styles.thumbnailPreview}>
                <img src={formData.thumbnailPreview} alt="Thumbnail Preview" />
              </div>
            )}
          </Group>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose} disabled={isLoading || isUploading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={isLoading || isUploading}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
